// ENG-7771: persist the manager's sender_policy delivery baseline across
// process restarts. ENG-7779 generalized the same file into a multi-concern
// store covering the two behaviour baselines with the identical
// wipe-on-manager-restart pathology (knownSlackBehaviourHashes / ENG-6035,
// knownMsTeamsBehaviourHashes / ENG-6024).
//
// The manager tracks a per-agent hash of the effective sender_policy
// (`agentState.knownSenderPolicyHashes`) so `decideSenderPolicyRestart` can
// bounce a persistent session when the policy changes (the slack-channel /
// teams-channel MCP children read SLACK_SENDER_POLICY and friends from
// process.env exactly once at boot). That Map was in-memory only, and the
// manager restarts on every deploy/self-update while the agent's tmux
// session survives - so the first poll after any manager restart hit the
// "first poll, establish baseline, never restart" branch and advanced the
// baseline WITHOUT restarting. A policy changed across a manager restart was
// therefore never delivered to the running process, the console showed the
// restriction as active, and nothing ever self-healed: a security control
// silently decorative (DreamCourts/Piper, 2026-07-15). The behaviour hashes
// shared the exact pathology - and the slack one carries SLACK_ALLOWED_USERS,
// an access-control gate (ENG-7779).
//
// This sidecar makes the baselines survive manager restarts. Two invariants
// the call sites MUST preserve:
//
//   1. The sidecar records the hash the running (or next-spawned) session is
//      believed to HAVE, not the hash the manager last SAW. While a restart
//      that delivers a new policy is still pending (idle-deferred, window-
//      gated), the sidecar deliberately LAGS the in-memory Map - it advances
//      only when the restart actually fires (post-stop), or when no live
//      consumer needs a restart (dead session, oneshot, non-claude-code, no
//      slack/msteams channel, or a non-restrictive first-poll baseline).
//      Never write the new hash at schedule time: a manager restart during
//      the defer window would then load a sidecar that already claims
//      delivery, and the lost restart would never be rescheduled - the exact
//      bug this file exists to fix, one layer down.
//
//   2. The format version is coupled to the hash semantics of EVERY concern
//      in the file: `computeSenderPolicyRestartHash` for senderPolicy, and
//      the canonicalJson-of-subset hashes for slackBehaviour /
//      msteamsBehaviour (extractSlackBehaviourSubset /
//      extractMsTeamsBehaviourSubset - e.g. adding a field like ENG-6464 did
//      changes those semantics). If any of them changes, bump FORMAT_VERSION
//      so stale sidecars are discarded wholesale instead of mismatching
//      every entry and restarting the whole fleet at once. A discarded
//      sidecar degrades to the first-poll fail-safe: agents with a
//      restrictive concern (sender policy, slack allowed_users) restart once
//      (fail closed), everything else re-baselines silently. The coupling is
//      deliberately coarse - one version for all concerns keeps the discard
//      story simple, at the cost of the occasional cross-concern restart
//      round on a semantics bump.
//
// The file is a single JSON object with one key per concern. Writes are
// atomic (rename(2)) - a torn write would parse as corrupt and needlessly
// restart every restrictive-policy agent on the host.

import { existsSync, readFileSync } from 'node:fs';
import { join } from 'node:path';

import { atomicWriteFileSync } from './atomic-write.js';

const BASELINE_FILENAME = 'sender-policy-baseline.json';

/**
 * Bump when the hash semantics of ANY concern change (see invariant 2) so
 * persisted hashes from older CLI versions are discarded instead of
 * mass-mismatching.
 */
export const SENDER_POLICY_BASELINE_VERSION = 1;

/**
 * One key per baseline concern. Existing prod sidecars predate the behaviour
 * concerns and carry only `senderPolicy` - absent keys load as empty maps,
 * which degrades to the first-poll fail-safe for those concerns only.
 */
export const BASELINE_CONCERNS = [
  'senderPolicy',
  'slackBehaviour',
  'msteamsBehaviour',
  // ENG-9802: the telegram chat policy (allowlist / private-chats-only / peer
  // kill switch). Same shape and same reason as senderPolicy — the MCP child
  // reads it once at boot, so a change has to be delivered by a respawn, and
  // the baseline has to survive a manager restart or every deploy would look
  // like a first poll and respawn the fleet (ENG-7771).
  'telegramPolicy',
  // ENG-9861: the agent's standing rules (ENG-9789). Same shape and same reason
  // again — CLAUDE.md is read once at session start, so a rule added mid-session
  // is on disk and inert until an unrelated restart, and the baseline has to
  // survive a manager restart or every deploy looks like a first poll.
  //
  // Deliberately added WITHOUT bumping the format version. The version is
  // coupled to the hash SEMANTICS of existing concerns (invariant 2) and none
  // of those changed; a new key is additive, and an older sidecar lacking it
  // loads as an empty map, degrading to the first-poll fail-safe for THIS
  // concern only. Bumping instead would discard every agent's senderPolicy and
  // telegramPolicy baseline and take the whole fleet through a fail-closed
  // restart round to add a key — the precedent set when telegramPolicy itself
  // was added.
  'agentRules',
] as const;
export type BaselineConcern = (typeof BASELINE_CONCERNS)[number];

export type DeliveryBaselineMaps = Record<BaselineConcern, Map<string, string>>;

export function createDeliveryBaselineMaps(): DeliveryBaselineMaps {
  return {
    senderPolicy: new Map(),
    slackBehaviour: new Map(),
    msteamsBehaviour: new Map(),
    telegramPolicy: new Map(),
    agentRules: new Map(),
  };
}

export function getSenderPolicyBaselineFile(configDir: string): string {
  return join(configDir, BASELINE_FILENAME);
}

/**
 * Hydrate `target` (per concern: agentId -> hash) with persisted entries.
 *
 * Missing files are silent (fresh host / first run after the fix ships).
 * Corrupt or version-mismatched files are LOGGED and treated as empty - the
 * degradation is a one-time fail-closed restart round for agents with a
 * restrictive concern, which is the safe direction, but silence here would
 * mask the cause of that restart wave.
 */
export function loadSenderPolicyBaseline(
  target: DeliveryBaselineMaps,
  configDir: string,
  log?: (msg: string) => void,
): void {
  const path = getSenderPolicyBaselineFile(configDir);
  if (!existsSync(path)) return;
  let parsed: unknown;
  try {
    parsed = JSON.parse(readFileSync(path, 'utf-8'));
  } catch (err) {
    log?.(
      `[sender-policy] discarding corrupt ${BASELINE_FILENAME} (${(err as Error).message}) - restrictive-policy agents will take one fail-closed restart`,
    );
    return;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    log?.(
      `[sender-policy] discarding malformed ${BASELINE_FILENAME} (not an object) - restrictive-policy agents will take one fail-closed restart`,
    );
    return;
  }
  const obj = parsed as Record<string, unknown>;
  if (obj.version !== SENDER_POLICY_BASELINE_VERSION) {
    log?.(
      `[sender-policy] discarding ${BASELINE_FILENAME} with version ${String(obj.version)} (expected ${SENDER_POLICY_BASELINE_VERSION}) - hash semantics changed; restrictive-policy agents will take one fail-closed restart`,
    );
    return;
  }
  for (const concern of BASELINE_CONCERNS) {
    const entries = obj[concern];
    if (!entries || typeof entries !== 'object' || Array.isArray(entries)) continue;
    for (const [agentId, hash] of Object.entries(entries as Record<string, unknown>)) {
      if (typeof hash === 'string' && hash.length > 0) target[concern].set(agentId, hash);
    }
  }
}

/**
 * Persist `source` to disk. Failures are non-fatal - the baselines stay in
 * memory and the next save may succeed. The cost of a lost write is one
 * fail-closed restart round after the next manager restart, never a
 * silently-unenforced policy.
 *
 * Returns whether the write landed, so the caller can keep a dirty flag and
 * retry a transiently-failed write on the next poll - without this the
 * in-memory mirror has already advanced, the identical-hash short-circuit
 * skips every subsequent save, and the sidecar stays stale until an
 * unrelated baseline changes (CodeRabbit PR #3432).
 */
export function saveSenderPolicyBaseline(
  source: DeliveryBaselineMaps,
  configDir: string,
  log?: (msg: string) => void,
): boolean {
  const path = getSenderPolicyBaselineFile(configDir);
  const payloadObj: Record<string, unknown> = { version: SENDER_POLICY_BASELINE_VERSION };
  for (const concern of BASELINE_CONCERNS) {
    const entries: Record<string, string> = {};
    for (const [agentId, hash] of source[concern]) entries[agentId] = hash;
    payloadObj[concern] = entries;
  }
  const payload = JSON.stringify(payloadObj, null, 2);
  try {
    atomicWriteFileSync(path, payload);
    return true;
  } catch (err) {
    log?.(
      `[sender-policy] failed to persist ${BASELINE_FILENAME} (non-fatal): ${(err as Error).message}`,
    );
    return false;
  }
}
